import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface Contact {
  _id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: 'new' | 'read' | 'in-progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category?: string;
  createdAt: string;
  response?: {
    message: string;
    respondedBy: string;
    respondedAt: string;
  };
}

const Contacts: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [isResponding, setIsResponding] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    search: ''
  });

  const getAuthToken = () => localStorage.getItem('token');

  useEffect(() => {
    fetchContacts();
  }, [filters]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      
      if (!token) {
        setError('Authentication required');
        return;
      }

      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.search) params.append('search', filters.search);

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/contact?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setContacts(data.data || []);
      } else {
        setError('Failed to fetch contacts');
      }
    } catch (err) {
      setError('Network error occurred');
      console.error('Contacts fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateContactStatus = async (contactId: string, newStatus: string, response?: string) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const updateData: any = { status: newStatus };
      if (response) updateData.response = response;

      const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/contact/${contactId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (res.ok) {
        await fetchContacts(); // Refresh the list
        setSelectedContact(null);
        setResponseMessage('');
      }
    } catch (err) {
      console.error('Update error:', err);
    }
  };

  const handleRespond = async () => {
    if (!selectedContact || !responseMessage.trim()) return;
    
    setIsResponding(true);
    await updateContactStatus(selectedContact._id, 'resolved', responseMessage);
    setIsResponding(false);
  };

  const deleteContact = async (contactId: string) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;

    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/contact/${contactId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await fetchContacts();
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'new': return 'bg-orange-100 text-orange-800';
      case 'read': return 'bg-blue-100 text-blue-800';
      case 'in-progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case 'low': return 'bg-gray-100 text-gray-800';
      case 'medium': return 'bg-blue-100 text-blue-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'urgent': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading contacts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <Link to="/admin/dashboard" className="text-green-600 hover:text-green-700 mb-2 inline-block">
                ← Back to Dashboard
              </Link>
              <h1 className="text-2xl font-bold text-gray-900">Contact Management</h1>
              <p className="text-gray-600">Manage contact form submissions</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                placeholder="Search by name, email, subject..."
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="">All Statuses</option>
                <option value="new">New</option>
                <option value="read">Read</option>
                <option value="in-progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <select
                value={filters.priority}
                onChange={(e) => setFilters({...filters, priority: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({status: '', priority: '', search: ''})}
                className="w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Contacts List */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Contact Messages ({contacts.length})</h2>
          </div>

          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <div className="divide-y divide-gray-200">
            {contacts.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-gray-400 text-6xl mb-4">📧</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No contacts found</h3>
                <p className="text-gray-600">No contact messages match your current filters.</p>
              </div>
            ) : (
              contacts.map((contact) => (
                <div key={contact._id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div>
                        <h3 className="text-lg font-semibold">{contact.name}</h3>
                        <p className="text-sm text-gray-600">{contact.email}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(contact.status)}`}>
                        {contact.status}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityBadgeClass(contact.priority)}`}>
                        {contact.priority} priority
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">
                        {new Date(contact.createdAt).toLocaleDateString()}
                      </span>
                      <button
                        onClick={() => setSelectedContact(contact)}
                        className="text-green-600 hover:text-green-700 font-medium"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => deleteContact(contact._id)}
                        className="text-red-600 hover:text-red-700 font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <h4 className="font-medium text-gray-900 mb-1">Subject: {contact.subject}</h4>
                    <p className="text-gray-700 line-clamp-2">{contact.message}</p>
                  </div>

                  {contact.response && (
                    <div className="mt-4 p-3 bg-green-50 rounded border-l-4 border-green-400">
                      <p className="text-sm font-medium text-green-800 mb-1">Response sent:</p>
                      <p className="text-sm text-green-700">{contact.response.message}</p>
                      <p className="text-xs text-green-600 mt-1">
                        Responded on {new Date(contact.response.respondedAt).toLocaleDateString()}
                      </p>
                    </div>
                  )}

                  <div className="mt-4 flex space-x-2">
                    {contact.status === 'new' && (
                      <button
                        onClick={() => updateContactStatus(contact._id, 'read')}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                      >
                        Mark as Read
                      </button>
                    )}
                    {contact.status !== 'resolved' && contact.status !== 'closed' && (
                      <button
                        onClick={() => updateContactStatus(contact._id, 'in-progress')}
                        className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700"
                      >
                        In Progress
                      </button>
                    )}
                    {contact.status !== 'closed' && (
                      <button
                        onClick={() => updateContactStatus(contact._id, 'closed')}
                        className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                      >
                        Close
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Contact Detail Modal */}
      {selectedContact && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Contact Details</h2>
                <button
                  onClick={() => setSelectedContact(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <h3 className="font-medium text-gray-900">Name</h3>
                <p className="text-gray-700">{selectedContact.name}</p>
              </div>
              
              <div>
                <h3 className="font-medium text-gray-900">Email</h3>
                <p className="text-gray-700">{selectedContact.email}</p>
              </div>
              
              <div>
                <h3 className="font-medium text-gray-900">Subject</h3>
                <p className="text-gray-700">{selectedContact.subject}</p>
              </div>
              
              <div>
                <h3 className="font-medium text-gray-900">Message</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedContact.message}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-medium text-gray-900">Status</h3>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(selectedContact.status)}`}>
                    {selectedContact.status}
                  </span>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Priority</h3>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityBadgeClass(selectedContact.priority)}`}>
                    {selectedContact.priority}
                  </span>
                </div>
              </div>
              
              <div>
                <h3 className="font-medium text-gray-900">Received</h3>
                <p className="text-gray-700">{new Date(selectedContact.createdAt).toLocaleString()}</p>
              </div>

              {!selectedContact.response && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Send Response</h3>
                  <textarea
                    value={responseMessage}
                    onChange={(e) => setResponseMessage(e.target.value)}
                    rows={4}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Type your response here..."
                  />
                  <button
                    onClick={handleRespond}
                    disabled={isResponding || !responseMessage.trim()}
                    className="mt-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400"
                  >
                    {isResponding ? 'Sending...' : 'Send Response'}
                  </button>
                </div>
              )}

              {selectedContact.response && (
                <div className="p-4 bg-green-50 rounded border-l-4 border-green-400">
                  <h3 className="font-medium text-green-800 mb-2">Response Sent</h3>
                  <p className="text-green-700 whitespace-pre-wrap">{selectedContact.response.message}</p>
                  <p className="text-xs text-green-600 mt-2">
                    Sent on {new Date(selectedContact.response.respondedAt).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;
